import React, { useCallback, Dispatch, SetStateAction } from 'react';
import { Box, CircularProgress } from '@mui/material';
import TextInputWithIcon from '../../components/TextInputWithIcon';
import { SnackbarSeverityType } from '../../enums';
import SearchStudentFeeEntryTable from './components/SearchStudentFeeEntryTable';
import { transformSearchStudentFeeEntryData } from './utils/transformSearchStudentFeeEntryData';
import { fetchAllSearchStudentFeeEntryReportPages } from './utils/searchStudentFeeEntryReportUtils';
import { ButtonBox, FilterColumn, FiltersContainer, StyledButton } from './styled';
import { StudentDetails } from './types';
import { PaperWrapper, Title } from '../common/styled';
import pallet from '../../layouts/pallet';

interface SearchStudentFeeEntryPageProps {
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  onFeeDetailsClick: (studentData: StudentDetails) => void;
  studentName: string;
  setStudentName: Dispatch<SetStateAction<string>>;
  enrollmentNo: string;
  setEnrollmentNo: Dispatch<SetStateAction<string>>;
  parentName: string;
  setParentName: Dispatch<SetStateAction<string>>;
  studentProfileData: StudentDetails[] | undefined;
  setStudentProfileData: Dispatch<SetStateAction<StudentDetails[] | undefined>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isSearched: boolean;
  setIsSearched: Dispatch<SetStateAction<boolean>>;
}

const SearchStudentFeeEntryPage: React.FC<SearchStudentFeeEntryPageProps> = ({
  onShowToast,
  onFeeDetailsClick,
  studentName,
  setStudentName,
  enrollmentNo,
  setEnrollmentNo,
  parentName,
  setParentName,
  studentProfileData,
  setStudentProfileData,
  isLoading,
  setIsLoading,
  isSearched,
  setIsSearched
}) => {
  const fetchStudentData = useCallback(async () => {
    setIsLoading(true);
    setIsSearched(false);
    try {
      const FEE_ENTRY_COLUMNS = [
        'student__enrollmentNo',
        'student__name',
        'student__grade',
        'student__division',
        'father__name',
        'mother__name',
        'student__email',
        'student__status',
        'student__type',
        'student__profile'
      ];

      const params = {
        name: studentName,
        enrollmentNo: enrollmentNo,
        parentName: parentName,
        selectedColumns: FEE_ENTRY_COLUMNS
      };

      const usersData = await fetchAllSearchStudentFeeEntryReportPages(params);
      const transformedData: StudentDetails[] =
        transformSearchStudentFeeEntryData(usersData);

      const trimmedParentName = parentName.trim().toLowerCase();
      const filteredData = trimmedParentName
        ? transformedData.filter(
            (student) =>
              student.fatherName?.toLowerCase().includes(trimmedParentName) ||
              student.motherName?.toLowerCase().includes(trimmedParentName)
          )
        : transformedData;

      setStudentProfileData(filteredData);
      setIsSearched(true);
    } catch (error: any) {
      console.error('Error fetching student data:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Error getting student details',
        SnackbarSeverityType.error
      );
      setStudentProfileData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    studentName,
    enrollmentNo,
    parentName,
    onShowToast,
    setIsLoading,
    setIsSearched,
    setStudentProfileData
  ]);

  const handleSearch = () => {
    fetchStudentData();
  };

  const handleFeeDetailsClick = (studentId: string) => {
    const selectedStudent = studentProfileData?.find(
      (student) => student.id === studentId
    );
    if (selectedStudent) {
      onFeeDetailsClick(selectedStudent);
    } else {
      onShowToast('Student data not found', SnackbarSeverityType.error);
    }
  };

  return (
    <>
      <Title>Search Student Fee Entry</Title>
      <Box sx={{ pt: 0 }}>
        <PaperWrapper sx={{ minHeight: 'auto !important' }}>
          <FiltersContainer>
            <FilterColumn>
              <TextInputWithIcon
                fullWidth
                size='small'
                value={studentName}
                onChange={(e) => {
                  setStudentName(e.target.value);
                  setIsSearched(false);
                }}
                label='Student Name'
                required={false}
                placeholder='Student Name'
                sx={{
                  '& .MuiInputBase-root': {
                    height: '50px'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: 1,
                    color: pallet.greyText
                  }
                }}
              />
            </FilterColumn>
            <FilterColumn>
              <TextInputWithIcon
                fullWidth
                size='small'
                value={enrollmentNo}
                onChange={(e) => {
                  setEnrollmentNo(e.target.value);
                  setIsSearched(false);
                }}
                label='Enrollment Number'
                required={false}
                placeholder='Enrollment Number'
                sx={{
                  '& .MuiInputBase-root': {
                    height: '50px'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: 1,
                    color: pallet.greyText
                  }
                }}
              />
            </FilterColumn>
            <FilterColumn>
              <TextInputWithIcon
                fullWidth
                size='small'
                value={parentName}
                onChange={(e) => {
                  setParentName(e.target.value);
                  setIsSearched(false);
                }}
                label="Parent's Name"
                required={false}
                placeholder="Father's / Mother's Name"
                sx={{
                  '& .MuiInputBase-root': {
                    height: '50px'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: 1,
                    color: pallet.greyText
                  }
                }}
              />
            </FilterColumn>
            <ButtonBox>
              <StyledButton
                onClick={handleSearch}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Search'
                )}
              </StyledButton>
            </ButtonBox>
          </FiltersContainer>
        </PaperWrapper>
        {isSearched && (
          <SearchStudentFeeEntryTable
            isLoading={isLoading}
            studentProfileData={studentProfileData || []}
            onFeeDetailsClick={handleFeeDetailsClick}
          />
        )}
      </Box>
    </>
  );
};

export default SearchStudentFeeEntryPage;
